/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _2 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>2 - Конструкторы</h2>
    <p className={styles.description}>
      Обычный синтаксис литерала объекта <code className={styles.code}>{`{}`}</code> позволяет создать один объект. Но
      зачастую нужно создать много однотипных объектов, динамичекски, во время выполнения программы. Для этого
      используют <b>функции-конструкторы</b>, вызывая их при помощи специального оператора{' '}
      <code className={styles.code}>new</code>.{' '}
    </p>
    <p className={styles.description}>
      <b>Конструктор</b> — это обычная функция к которой применили оператор <code className={styles.code}>new</code>.
      Это приводит к созданию нового объекта и вызову функции в контексте этого объекта.{' '}
    </p>
    <p className={styles.notificationInfo}>
      Любая функция, кроме стрелочной, может быть использована как конструктор, то есть вызвана при помощи оператора{' '}
      <code className={styles.code}>new</code>. При вызове стрелки через <code className={styles.code}>new</code> будет
      ошибка.{' '}
    </p>
    <p className={styles.description}>
      Чтобы отличить конструктор от обычной функции, конструкторы принято называть с большой буквы, а в самом названии
      отражать тип создаваемого объекта. Мы уже работали с конструкторами встроенными в язык:{' '}
      <code className={styles.code}>Object</code>, <code className={styles.code}>Array</code>,{' '}
      <code className={styles.code}>Number</code>, <code className={styles.code}>String</code> и другие.{' '}
    </p>
    <p className={styles.description}>Процесс создания объекта через конструтор происходит следующим образом: </p>
    <ul className={styles.list}>
      <li className={styles.list__item}>Создаётся новый пустой объект.</li>
      <li className={styles.list__item}>Функция-конструктор вызывается в контексте этого объекта.</li>
      <li className={styles.list__item}>
        Ключевое слово <code className={styles.code}>this</code> внутри конструктора получает ссылку на этот объект.
      </li>
      <li className={styles.list__item}>
        Конструктор выполняется и, как правило, модифицирует <code className={styles.code}>this</code> (созданный
        объект), добавляя свойства и методы.
      </li>
      <li className={styles.list__item}>
        Возвращается <code className={styles.code}>this</code>.
      </li>
    </ul>
    <CopyBlock
      text={`const Hotel = function (name, stars, capacity) {
  this.name = name;
  this.stars = stars;
  this.capacity = capacity;
};

// В результате вызова
const hotel = new Hotel('Resort Hotel', 5, 100);
// Получаем такой объект
console.log(hotel);
// Hotel {name: "Resort Hotel", stars: 5, capacity: 100}`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>
      Если визуализировать происходящее, то при вызове <code className={styles.code}>new Hotel</code> происходит
      следующее (первая и последняя строка — это то, что делает интерпретатор).
    </p>
    <CopyBlock
      text={`const Hotel = function (name, stars, capacity) {
  // this = {};

  // в this пишем свойства и методы
  this.name = name;
  this.stars = stars;
  this.capacity = capacity;

  // return this;
};`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>
      Теперь многократными вызовами <code className={styles.code}>new Hotel</code> с разными аргументами мы можем
      создать любое количество объектов. Использование конструкторов удобно при созданнии множества объектов с одним
      набором свойств имеющих разные значения. Поэтому такую функцию и называют конструктором — она предназначена для
      конструирования объектов по заранее подготовленнному шаблону.{' '}
    </p>
    <CopyBlock
      text={`const Hotel = function (name, stars, capacity) {
  this.name = name;
  this.stars = stars;
  this.capacity = capacity;
};

const hotel = new Hotel('Resort Hotel', 5, 100);
console.log(hotel);
// Hotel {name: "Resort Hotel", stars: 5, capacity: 100}

const motel = new Hotel('Sunlight Motel', 4, 300);
console.log(motel);
// Hotel {name: "Sunlight Motel", stars: 4, capacity: 300}`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.1 - Внутренние методы [[Call]] и [[Construct]]</h3>
      <p className={styles.description}>
        Функции вызываются используя два разных внутренних метода: <code className={styles.code}>[[Call]]</code> и{' '}
        <code className={styles.code}>[[Construct]]</code>.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Когда функция вызывается без <code className={styles.code}>new</code>, выполняется метод{' '}
          <code className={styles.code}>[[Call]]</code>, который исполняет тело функции так, как оно описано в коде.
        </li>
        <li className={styles.list__item}>
          Когда функция вызывается с <code className={styles.code}>new</code>, выполняется метод{' '}
          <code className={styles.code}>[[Construct]]</code>, который отвечает за создание нового объекта и исполнения
          тела функции с <code className={styles.code}>this</code> ссылающимся на этот объект.
        </li>
      </ul>
      <p className={styles.description}>
        Не все функции имеют внутренний метод <code className={styles.code}>[[Construct]]</code>, и поэтому не все
        функции могут быть вызваны через <code className={styles.code}>new</code>. Стрелочные функции не имеют метода{' '}
        <code className={styles.code}>[[Construct]]</code> и, поэтому, не могут быть использованы в качестве
        конструкторов.{' '}
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.2 - Добавление методов</h3>
      <p className={styles.description}>
        Использование функций для создания объекта дает большую гибкость. Можно передавать конструктору параметры,
        определяющие как его создавать, и он будет создавать объекты заданным образом.{' '}
      </p>
      <p className={styles.description}>
        Добавим метод в создаваемый объект. Например, пусть <code className={styles.code}>Hotel</code> имеет методы для
        приветствия гостя и добавления и удаления количества гостей в поле{' '}
        <code className={styles.code}>guestCount</code>. Проверки на вместительность пропустим.{' '}
      </p>
      <CopyBlock
        text={`const Hotel = function (name, stars, capacity) {
  this.name = name;
  this.stars = stars;
  this.capacity = capacity;
  this.guestCount = 0;

  this.greet = function (guestName) {
    console.log(\`Hello $ {guestName}, wellcome to $ {this.name}\`);
  };

  this.addGuests = function (amount) {
    this.guestCount += amount;
  };

  this.removeGuests = function (amount) {
    this.guestCount -= amount;
  };
};

const hotel = new Hotel('Sunrise Hotel', 5, 100);

console.log(hotel);
// Hotel {name: "Sunrise Hotel", greet: ƒ, addGuests: f, removeGuests: f}
hotel.greet('Mango'); // Hello Mango, wellcome to Sunrise Hotel
hotel.addGuests(50);
hotel.removeGuests(50);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.notificationInfo}>
        В этом разделе небыло упоминания о записи методов конструктора в прототип, эта тема рассматривается дальше. На
        этом этапе, нам важно просто уметь создавать объекты по шаблону и понимать принцип работы контекста и
        функций-конструкторов.{' '}
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.3 - Управляем магазином</h3>
      <p className={styles.description}>
        Время практики. Представьте что у нас есть магазин и два менеджера, по продаже товаров. Необходимо написать
        логику для организации их работы. Для каждого менеджера создаётся отдельный объект.{' '}
      </p>
      <CopyBlock
        text={`const mango = {
  name: 'Mango',
  sales: 5,
  sell(product) {
    this.sales += 1;
    return \`Manager $ {this.name} sold $ {product}.\`;
  },
};

console.log(mango.sales); // 5
console.log(mango.sell('TV')); // Manager Mango sold TV
console.log(mango.sell('Microwave')); // Manager Mango sold Microwave
console.log(mango.sales); // 7

const poly = {
  name: 'Poly',
  sales: 10,
  sell(product) {
    this.sales += 1;
    return \`Manager $ {this.name} sold $ {product}.\`;
  },
};

console.log(poly.sales); // 10
console.log(poly.sell('TV')); // Manager Poly sold TV
console.log(poly.sell('Microwave')); // Manager Poly sold Microwave
console.log(poly.sales); // 12`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Это было просто и всё работает хорошо, но мы буквально захардкодили менеджеров заранее. Представьте, что будет,
        когда наш магазин вырастет и пополнится парой сотен менеджеров, для каждого из которых будет необходимо создать
        объект, динамически, во время выполнения программы. Очевидно, что создавать объекты подобным способом — не самая
        лучшая затея. К счастью мы знаем о конструкторах.{' '}
      </p>
      <CopyBlock
        text={`const Manager = function (name = 'manager', sales = 0) {
  this.name = name;
  this.sales = sales;

  this.sell = function (product) {
    this.sales += 1;
    return \`Manager $ {this.name} sold $ {product}.\`;
  };
};

const mango = new Manager('Mango', 5);
console.log(mango.sales); // 5
console.log(mango.sell('TV')); // Manager Mango sold TV
console.log(mango.sell('Microwave')); // Manager Mango sold Microwave
console.log(mango.sales); // 7

const poly = new Manager('Poly', 10);
console.log(poly.sales); // 10
console.log(poly.sell('TV')); // Manager Poly sold TV
console.log(poly.sell('Microwave')); // Manager Poly sold Microwave
console.log(poly.sales); // 12`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Теперь, когда мы узнаем как работать с HTML-документом, нам не страшно любое количество менеджеров. Собрав поля
        из форм регистрации можно динамически создавать объекты по введенной информации.{' '}
      </p>
    </section>
  </section>
))
